import { useQuery } from '@apollo/client/react';
import { SETTINGS_QUERY } from '../graphql/queries';

interface Setting {
  id: number;
  key: string;
  value: unknown;
}

interface SettingsData {
  settings: Setting[];
}

/**
 * One global setting, by key.
 *
 * `value` is `Mixed` on the API — a setting holds whatever was typed into the
 * catalogue, so a number arrives as a string as often as not. Callers say what
 * they expect: `useNumericSetting` for the ones that are figures.
 *
 * `loading` matters to the caller. A form that seeds a field from a setting has
 * to wait for it, or the field is filled with the fallback and then silently
 * changes under whoever is already typing.
 */
export function useSetting(key: string): { value: unknown; loading: boolean } {
  const { data, loading } = useQuery<SettingsData>(SETTINGS_QUERY);
  const setting = data?.settings.find((candidate) => candidate.key === key);
  return { value: setting?.value, loading };
}

/** A setting read as a number, or `null` where it is missing or not one. */
export function useNumericSetting(key: string): { value: number | null; loading: boolean } {
  const { value, loading } = useSetting(key);
  const numeric = typeof value === 'number' ? value : Number(value);
  return {
    value:
      value === undefined || value === null || value === '' || Number.isNaN(numeric)
        ? null
        : numeric,
    loading,
  };
}
